'use client';

import React, { useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import { API_CONFIG } from '@/config/api.config';
import { sendEmail } from '@/app/report/services/api';
import styles from '../infographics.module.css';
import { getDialCode } from '@/constants/countryDialCodes';
import { useSnackbar } from '@/context/SnackbarContext';

// ISO-2 country code map for use with react-phone-number-input
const COUNTRY_ISO2: Record<string, string> = {
    "US": "US", "GB": "GB", "CA": "CA", "AU": "AU", "IN": "IN",
    "DE": "DE", "FR": "FR", "JP": "JP", "CN": "CN", "BR": "BR",
    "MX": "MX", "IT": "IT", "ES": "ES", "NL": "NL", "SE": "SE",
    "SG": "SG", "AE": "AE", "ZA": "ZA",
};

// Lazy load reCAPTCHA to reduce initial bundle and fix SSR issues
const ReCAPTCHA = dynamic(() => import('react-google-recaptcha'), {
    ssr: false,
    loading: () => <div style={{ height: '78px', background: '#f9f9f9', borderRadius: '4px' }} />
}) as any;

interface InfographicContactFormProps {
    title?: string;
    showSubtitle?: boolean;
}

export default function InfographicContactForm({
    title = "Get More Insights On The Infographics",
    showSubtitle = true
}: InfographicContactFormProps) {
    const { showSnackbar } = useSnackbar();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        country: '',
        message: ''
    });

    // Status state for UI feedback
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [isInteracted, setIsInteracted] = useState(false);
    const [phoneCountry, setPhoneCountry] = useState<string>('US');

    const recaptchaRef = useRef<any>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === 'phone') {
            const onlyNums = value.replace(/\D/g, '').slice(0, 20);
            setFormData(prev => ({ ...prev, [name]: onlyNums }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate reCAPTCHA FIRST before changing status
        if (!captchaToken) {
            showSnackbar('Please complete the reCAPTCHA verification', 'error');
            return;
        }

        // Reset status
        setStatus('submitting');
        setErrorMessage('');

        try {
            const dialCode = getDialCode(formData.country);
            const fullPhone = dialCode ? `${dialCode} ${formData.phone}` : formData.phone;

            // Construct payload for Universal Email Service
            const payload = {
                form_type: "Infographic Inquiry",
                recipient_email: formData.email,
                recipient_name: formData.name,
                form_data: {
                    ...formData,
                    phone: fullPhone,
                    Source: window.location.href
                }
            };

            await sendEmail(payload);

            // Success handling
            setStatus('success');
            showSnackbar('Thank you for your interest! We will contact you soon.', 'success');

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                country: '',
                message: ''
            });
            setCaptchaToken(null);
            recaptchaRef.current?.reset();

            // Reset status after a delay
            setTimeout(() => setStatus('idle'), 3000);

        } catch (error: any) {
            console.error("Submission Error:", error);
            setStatus('error');
            setErrorMessage(error.message || 'Failed to submit form. Please try again.');
            showSnackbar(error.message || 'Failed to submit form. Please try again.', 'error');
        } finally {
            setStatus('idle');
        }
    };

    return (
        <div className={styles.contactForm}>
            <h3 className={styles.formTitle}>{title}</h3>
            {showSubtitle && (
                <p className={styles.formSubtitle}>
                    If you are unable to submit the form, kindly email{' '}
                    <a href="mailto:inbound@tbrc.info">inbound@tbrc.info</a>
                </p>
            )}

            <form onSubmit={handleSubmit} onFocus={() => setIsInteracted(true)} onMouseEnter={() => setIsInteracted(true)}>
                <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required
                />

                <input
                    type="email"
                    name="email"
                    placeholder="Your company email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required
                />

                <div className={styles.phoneInputGroup}>
                    <div className={styles.countryCode}>
                        {getDialCode(formData.country) || '--'}
                    </div>
                    <input
                        type="text"
                        name="phone"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`${styles.formInput} ${styles.phoneInput}`}
                        required
                    />
                </div>

                <input
                    type="text"
                    name="company"
                    placeholder="Company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required
                />

                <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className={styles.formSelect}
                    required
                >
                    <option value="" disabled>Country</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="IN">India</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="JP">Japan</option>
                    <option value="CN">China</option>
                    <option value="BR">Brazil</option>
                    <option value="MX">Mexico</option>
                    <option value="IT">Italy</option>
                    <option value="ES">Spain</option>
                    <option value="NL">Netherlands</option>
                    <option value="SE">Sweden</option>
                    <option value="SG">Singapore</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="ZA">South Africa</option>
                </select>

                <textarea
                    name="message"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={styles.formTextarea}
                    rows={4}
                />

                <div className={styles.recaptchaWrapper}>
                    {isInteracted && (
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={API_CONFIG.RECAPTCHA_SITE_KEY}
                            theme="light"
                            onChange={(token: string | null) => setCaptchaToken(token)}
                        />
                    )}
                </div>

                <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={status === 'submitting'}
                    style={status === 'submitting' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                >
                    {status === 'submitting' ? 'Sending...' : 'Submit'}
                </button>
                {status === 'error' && (
                    <div style={{ color: '#ff4444', marginTop: '10px', fontSize: '14px', textAlign: 'center' }}>
                        {errorMessage}
                    </div>
                )}
            </form>
        </div>
    );
}
